import { useQuery } from "@tanstack/react-query";
import {
	API_URL,
	type ApiOrgContext,
	apiErrorFor,
	fetchWithTimeout,
	resolveApiScope,
} from "./core";

/**
 * Agent Permissions — the deterministic write-gate "Actor × Action → Effect"
 * matrix for a single agent. This is the hard, server-enforced grant model, NOT
 * the probabilistic LLM-judge "Guardrails" tab. Keep the two distinct.
 */

/** A resource class the gate can grant on. */
export type PermissionResourceClass =
	| "entity"
	| "connector_action"
	| "agent_config"
	| "entity_schema";

/**
 * The verbs an action row can carry. `read`/`create`/`update`/`delete` are shared
 * by entity and agent_config; `execute` governs connector operations;
 * the `*_type` verbs belong to entity_schema and govern type DEFINITIONS, not the
 * entity records typed by them.
 */
export type PermissionAction =
	| "read"
	| "create"
	| "update"
	| "delete"
	| "execute"
	| "create_type"
	| "update_type"
	| "delete_type"
	| "create_relationship_type"
	| "update_relationship_type"
	| "delete_relationship_type";

/** One approval decision. Auto still enforces auth and guardrails. */
export type PermissionEffect = "auto" | "approval" | "deny";

export const EFFECT_STRICTNESS: Record<PermissionEffect, number> = {
	auto: 0,
	approval: 1,
	deny: 2,
};

export type OperationCategory =
	| "read"
	| "write"
	| "destructive"
	| "non_destructive"
	| "unknown";

interface ConnectorPolicyScope {
	connector_key?: string | null;
	connection_id?: number | null;
	operation_category?: OperationCategory | null;
}

export interface ConnectorPolicyRule extends ConnectorPolicyScope {
	operation_key?: string | null;
	effect: PermissionEffect;
}
interface ConnectorPolicyCollection {
	revision: string;
	rules: ConnectorPolicyRule[];
}

interface ConnectorPolicyExplanation {
	effect: PermissionEffect;
	rule_ids: number[];
	reason: string;
}

/**
 * A serialized policy row (from `serializeEntityApprovalPolicy`). Both the org
 * floor rows (principal_kind === null) and this agent's rows share this shape.
 */
export interface AgentPermissionPolicy extends ConnectorPolicyScope {
	id: number;
	resource_class: PermissionResourceClass;
	principal_kind: string | null;
	principal_id: string | null;
	/** Connector operation this row scopes to (connector_action only); null = the
	 * blanket execute row governing every operation. */
	operation_key: string | null;
	/** agent_config exception: target agents.id; null = any target. */
	target_agent_id: string | null;
	entity_type_slug: string | null;
	field_path: string | null;
	entity_id: number | null;
	create_mode: PermissionEffect;
	update_mode: PermissionEffect;
	delete_mode: PermissionEffect;
	effects: Partial<Record<PermissionAction, PermissionEffect>>;
	approval_connection_id: string | null;
	approval_channel_id: string | null;
	approval_team_id: string | null;
	approval_channel_name: string | null;
}

export interface AgentPermissionEntityType {
	slug: string;
	name: string;
	/** Lucide name or emoji from entity_types.icon; null when unset. */
	icon?: string | null;
}

/** A connector operation available to the policy editor. */
export interface AgentPermissionConnectorOperation {
	operation_key: string;
	name: string;
	connector_key: string;
	connector_name: string;
	/** Declared read/write classification. */
	kind?: "read" | "write";
	destructive?: boolean | null;
}

export interface AgentPermissionAgentRef {
	id: string;
	name: string;
}

export interface AgentPermissionsResponse {
	connectors?: Array<{ key: string; name: string }>;
	connections?: Array<{
		id: number;
		connector_key: string;
		display_name: string | null;
	}>;
	operation_categories?: OperationCategory[];
	floor: AgentPermissionPolicy[];
	agent: AgentPermissionPolicy[];
	entity_types: AgentPermissionEntityType[];
	/** Operations for policy scopes. */
	connector_operations: AgentPermissionConnectorOperation[];
	/** Agents in the org for agent_config target exceptions. */
	agents: AgentPermissionAgentRef[];
}

interface AgentPermissionUpdateInput extends ConnectorPolicyScope {
	resource_class: PermissionResourceClass;
	effects: Partial<Record<PermissionAction, PermissionEffect>>;
	/** Set for a per-type entity override; null/omit = the blanket "All types" default. */
	entity_type_slug?: string | null;
	/** Set for a per-operation connector override; null/omit = the blanket "execute" row. */
	operation_key?: string | null;
	/** agent_config: exception for update/delete of this target agent. */
	target_agent_id?: string | null;
}

interface AgentPermissionDeleteInput extends ConnectorPolicyScope {
	resource_class: PermissionResourceClass;
	entity_type_slug?: string | null;
	operation_key?: string | null;
	target_agent_id?: string | null;
}

function permissionsEndpoint(slug: string, agentId: string): string {
	return `${API_URL}/api/${slug}/agent/${encodeURIComponent(agentId)}/permissions`;
}

/** React Query key for one agent's permissions matrix. */
export function agentPermissionsQueryKey(
	orgContext: string | ApiOrgContext | null | undefined,
	agentId: string,
): (string | null)[] {
	const slug =
		typeof orgContext === "string" ? orgContext : (orgContext?.slug ?? null);
	return ["agent-permissions", slug, agentId];
}

export function useAgentPermissions(
	orgContext: string | ApiOrgContext | null | undefined,
	agentId: string,
	options?: { enabled?: boolean },
) {
	return useQuery({
		queryKey: agentPermissionsQueryKey(orgContext, agentId),
		enabled: (options?.enabled ?? true) && Boolean(agentId),
		queryFn: async () => {
			const scope = resolveApiScope(orgContext ?? undefined);
			const response = await fetchWithTimeout(
				permissionsEndpoint(scope.slug, agentId),
				{ credentials: "include" },
			);
			if (!response.ok) {
				throw await apiErrorFor(response);
			}
			return (await response.json()) as AgentPermissionsResponse;
		},
		staleTime: 30000,
	});
}

export async function updateAgentPermission(
	orgContext: string | ApiOrgContext,
	agentId: string,
	input: AgentPermissionUpdateInput,
): Promise<AgentPermissionPolicy> {
	const scope = resolveApiScope(orgContext);
	const response = await fetchWithTimeout(
		permissionsEndpoint(scope.slug, agentId),
		{
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify(input),
		},
	);
	if (!response.ok) {
		throw await apiErrorFor(response);
	}
	const result = (await response.json()) as {
		policy?: AgentPermissionPolicy;
	};
	if (!result.policy) {
		throw new Error("Agent permission update did not return a policy.");
	}
	return result.policy;
}

export async function deleteAgentPermission(
	orgContext: string | ApiOrgContext,
	agentId: string,
	input: AgentPermissionDeleteInput,
): Promise<boolean> {
	const scope = resolveApiScope(orgContext);
	const params = permissionScopeParams(input);
	const response = await fetchWithTimeout(
		`${permissionsEndpoint(scope.slug, agentId)}?${params.toString()}`,
		{ method: "DELETE", credentials: "include" },
	);
	if (!response.ok) {
		throw await apiErrorFor(response);
	}
	const result = (await response.json()) as { deleted?: boolean };
	return result.deleted === true;
}

// ---------------------------------------------------------------------------
// Org floor write-permissions (principal null) — same matrix shape as agent.
// GET already returns { floor: [], agent: floorRows } so the shared model works.
// ---------------------------------------------------------------------------

function orgWritePermissionsEndpoint(slug: string): string {
	return `${API_URL}/api/${slug}/write-permissions`;
}

export function orgWritePermissionsQueryKey(
	orgContext: string | ApiOrgContext | null | undefined,
): (string | null)[] {
	const slug =
		typeof orgContext === "string" ? orgContext : (orgContext?.slug ?? null);
	return ["write-permissions", slug];
}

export function useOrgWritePermissions(
	orgContext: string | ApiOrgContext | null | undefined,
	options?: { enabled?: boolean },
) {
	return useQuery({
		queryKey: orgWritePermissionsQueryKey(orgContext),
		enabled: options?.enabled ?? true,
		queryFn: async () => {
			const scope = resolveApiScope(orgContext ?? undefined);
			const response = await fetchWithTimeout(
				orgWritePermissionsEndpoint(scope.slug),
				{ credentials: "include" },
			);
			if (!response.ok) {
				throw await apiErrorFor(response);
			}
			return (await response.json()) as AgentPermissionsResponse;
		},
		staleTime: 30000,
	});
}

export function orgConnectorPoliciesQueryKey(owner: string) {
	return [...orgWritePermissionsQueryKey(owner), "connector-actions"];
}

export function useOrgConnectorPolicies(owner: string, enabled = true) {
	return useQuery({
		queryKey: orgConnectorPoliciesQueryKey(owner),
		enabled,
		queryFn: async () => {
			const scope = resolveApiScope(owner);
			const response = await fetchWithTimeout(
				`${orgWritePermissionsEndpoint(scope.slug)}/connector-actions`,
				{ credentials: "include" },
			);
			if (!response.ok) throw await apiErrorFor(response);
			return (await response.json()) as ConnectorPolicyCollection;
		},
	});
}

export async function replaceOrgConnectorPolicies(
	owner: string,
	collection: ConnectorPolicyCollection,
): Promise<ConnectorPolicyCollection> {
	const scope = resolveApiScope(owner);
	const response = await fetchWithTimeout(
		`${orgWritePermissionsEndpoint(scope.slug)}/connector-actions`,
		{
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify(collection),
		},
	);
	if (!response.ok) throw await apiErrorFor(response);
	return (await response.json()) as ConnectorPolicyCollection;
}

export async function updateOrgWritePermission(
	orgContext: string | ApiOrgContext,
	input: AgentPermissionUpdateInput & {
		resource_class: Exclude<PermissionResourceClass, "connector_action">;
	},
): Promise<AgentPermissionPolicy> {
	const scope = resolveApiScope(orgContext);
	const response = await fetchWithTimeout(
		orgWritePermissionsEndpoint(scope.slug),
		{
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			credentials: "include",
			body: JSON.stringify(input),
		},
	);
	if (!response.ok) {
		throw await apiErrorFor(response);
	}
	const result = (await response.json()) as {
		policy?: AgentPermissionPolicy;
	};
	if (!result.policy) {
		throw new Error("Org write permission update did not return a policy.");
	}
	return result.policy;
}

export async function deleteOrgWritePermission(
	orgContext: string | ApiOrgContext,
	input: AgentPermissionDeleteInput & {
		resource_class: Exclude<PermissionResourceClass, "connector_action">;
	},
): Promise<boolean> {
	const scope = resolveApiScope(orgContext);
	const params = permissionScopeParams(input);
	const response = await fetchWithTimeout(
		`${orgWritePermissionsEndpoint(scope.slug)}?${params.toString()}`,
		{ method: "DELETE", credentials: "include" },
	);
	if (!response.ok) {
		throw await apiErrorFor(response);
	}
	const result = (await response.json()) as { deleted?: boolean };
	return result.deleted === true;
}

function permissionScopeParams(
	input: AgentPermissionDeleteInput,
): URLSearchParams {
	const params = new URLSearchParams({ resource_class: input.resource_class });
	for (const key of [
		"entity_type_slug",
		"operation_key",
		"target_agent_id",
		"connector_key",
		"connection_id",
		"operation_category",
	] as const) {
		if (input[key] != null) params.set(key, String(input[key]));
	}
	return params;
}

export function useConnectorPolicyExplanation(
	owner: string,
	connectionId: number | undefined,
	operationKey: string,
	agentId?: string,
) {
	return useQuery({
		queryKey: [
			"policy-explanation",
			owner,
			connectionId,
			operationKey,
			agentId,
		],
		enabled: Boolean(connectionId && operationKey),
		queryFn: async () => {
			const scope = resolveApiScope(owner);
			const params = new URLSearchParams({
				connection_id: String(connectionId),
				operation_key: operationKey,
			});
			if (agentId) params.set("agent_id", agentId);
			const response = await fetchWithTimeout(
				`${orgWritePermissionsEndpoint(scope.slug)}/explain?${params}`,
				{ credentials: "include" },
			);
			if (!response.ok) throw await apiErrorFor(response);
			return (await response.json()) as ConnectorPolicyExplanation;
		},
	});
}
